import React, { useState } from 'react';
import { Mail, Phone, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { contactAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await contactAPI.submit(formData);
      if (result.success) {
        toast({
          title: 'Message sent!',
          description: result.message
        });
        setFormData({ name: '', email: '', subject: '', message: '' });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to send message. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 via-pink-100 to-pink-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            Get in Touch
          </h1>
          <p className="text-xl text-gray-700 max-w-2xl mx-auto">
            Have questions or need custom solutions? We'd love to hear from you.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-12 max-w-3xl mx-auto">
          <Card className="text-center border-pink-200 hover:shadow-xl transition-shadow bg-white/60 backdrop-blur-sm">
            <CardHeader>
              <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-gray-900">Email Us</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 font-medium">contact@quickstatementconverter.com</p>
              <p className="text-sm text-gray-600 mt-2">We'll respond within 24 hours</p>
            </CardContent>
          </Card>

          <Card className="text-center border-pink-200 hover:shadow-xl transition-shadow bg-white/60 backdrop-blur-sm">
            <CardHeader>
              <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Phone className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-gray-900">Call Us</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 font-medium">786 390 2822</p>
              <p className="text-sm text-gray-600 mt-2">Mon-Fri, 9am-6pm EST</p>
            </CardContent>
          </Card>
        </div>

        <Card className="max-w-3xl mx-auto border-pink-200 shadow-xl bg-white/60 backdrop-blur-sm">
          <CardHeader>
            <CardTitle className="text-2xl text-gray-900">Send us a Message</CardTitle>
            <CardDescription className="text-gray-700">
              Fill out the form below and we'll get back to you as soon as possible.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={handleChange}
                    className="border-pink-200 focus:border-pink-400 focus:ring-pink-400"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="border-pink-200 focus:border-pink-400 focus:ring-pink-400"
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">Subject</Label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  placeholder="How can we help?"
                  value={formData.subject}
                  onChange={handleChange}
                  className="border-pink-200 focus:border-pink-400 focus:ring-pink-400"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message">Message</Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Tell us more about your needs..."
                  value={formData.message}
                  onChange={handleChange}
                  className="border-pink-200 focus:border-pink-400 focus:ring-pink-400 min-h-32"
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-pink-400 to-pink-300 hover:from-pink-500 hover:to-pink-400 text-white shadow-md hover:shadow-lg transition-all"
              >
                <Send className="w-4 h-4 mr-2" />
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
          </CardContent>
        </Card>

        <div className="mt-12 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Need Immediate Help?
          </h2>
          <p className="text-gray-600 mb-6">
            Check out our FAQ section or email us directly for urgent matters.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Contact;